import { PageTypes } from '#shared/resolve-page';
import {
    aboutNodes,
    breadcrumbListNode,
    collectionPageNode,
    itemListNode,
    jsonLdGraph,
    type BreadcrumbInput,
} from '~/utils/schemaOrg';
import {
    absoluteUrl,
    buildPageMetaTitle,
    hasListingQueryParams,
    stripHtml,
    trimTrailingSlash,
    truncate,
} from '~/utils/seo';

export type CollectionPageType = 'category' | 'brand' | 'brand_category';

export interface CollectionPageSchemaOptions {
    headline: MaybeRefOrGetter<string | undefined>;
    products: MaybeRefOrGetter<any[]>;
    breadcrumbs: MaybeRefOrGetter<BreadcrumbInput[]>;
    categoryName?: MaybeRefOrGetter<string | undefined>;
    brandName?: MaybeRefOrGetter<string | undefined>;
    startPosition?: MaybeRefOrGetter<number>;
    enabled?: boolean;
}

export function resolveCollectionPageType(type?: string): CollectionPageType | undefined {
    switch (type) {
        case PageTypes.Navigation:
            return 'category';
        case PageTypes.ManufacturerDetail:
            return 'brand';
        case PageTypes.ManufacturerCategory:
            return 'brand_category';
        default:
            return undefined;
    }
}


export default function useCollectionPageSchema(options: CollectionPageSchemaOptions) {
    if (options.enabled === false) return;

    const route = useRoute();
    const config = useRuntimeConfig();
    const pageDataStore = usePageDataStore();
    const snippetsStore = useSnippetsStore();

    const baseUrl = trimTrailingSlash(String(config.public.prodUrl ?? ''));
    if (!baseUrl) return;

    const pageType = resolveCollectionPageType((pageDataStore.data as any)?.type);
    if (!pageType) return;

    const canonicalUrl = useCanonicalUrl();
    const siteUrl = `${baseUrl}/nl/`;

    const includeItemList = !hasListingQueryParams(route.query);

    const graph = computed(() => {
        const seo = (pageDataStore.data as any)?.seo ?? {};
        const headline = toValue(options.headline) ?? '';
        const brandName = toValue(options.brandName);
        const breadcrumbs = toValue(options.breadcrumbs) ?? [];
        const brandCrumb = brandName
            ? breadcrumbs.find(crumb => crumb?.name?.toLowerCase() === brandName.toLowerCase() && !!crumb?.url)
            : undefined;
        const brandUrl = brandCrumb?.url
            ? absoluteUrl(baseUrl, brandCrumb.url)
            : pageType === 'brand'
              ? canonicalUrl
              : undefined;

        const description = seo.metaDescription || truncate(stripHtml(seo.topContent), 300);

        const itemList = includeItemList
            ? itemListNode({
                  baseUrl,
                  canonicalUrl,
                  name: headline,
                  products: toValue(options.products) ?? [],
                  startPosition: toValue(options.startPosition),
              })
            : undefined;

        return jsonLdGraph([
            collectionPageNode({
                siteUrl,
                canonicalUrl,
                name: buildPageMetaTitle(seo.metaTitle, (snippetsStore.snippets as any)?.['headless.metatitle']),
                headline,
                description,
                about: aboutNodes({
                    pageType,
                    categoryName: toValue(options.categoryName),
                    brandName,
                    brandUrl,
                }),
                hasItemList: !!itemList,
            }),
            breadcrumbListNode({ baseUrl, canonicalUrl, items: breadcrumbs }),
            itemList,
        ]);
    });

    useHead({
        script: [
            {
                key: 'schema-org-collection-page',
                type: 'application/ld+json',
                innerHTML: graph,
            },
        ],
    });
}
